import type { ClosingPackageJournalEntry } from '../types';
import { ORIGINALS_PROBLEM_TYPE_OPTIONS } from './originalsProblemTypes';

/**
 * Closing Package Timeline v1 view-model (FE-005A).
 * Source of truth: composition journal entries. Does not compute workflowState.
 */

export type ClosingPackageTimelineEventType =
  | 'INITIAL_COMPOSITION_CREATED'
  | 'CONFIRM_FORMATION'
  | 'SCAN_DISPATCH_REGISTERED'
  | 'CLIENT_APPROVED'
  | 'CLIENT_REJECTED'
  | 'ORIGINALS_PREPARED'
  | 'ORIGINALS_DISPATCHED'
  | 'CLIENT_RECEIVED'
  | 'ORIGINALS_RECEIPT_PROBLEM'
  | 'ORIGINALS_REVISION_CONFIRMED'
  | 'EDO_SENT'
  | 'EDO_RECEIPT_CONFIRMED'
  | 'EDO_CONTENT_REJECTED'
  | 'UNKNOWN';

export type ClosingPackageTimelineResult = 'success' | 'rejection' | 'problem' | 'neutral';

export type ClosingPackageTimelineItem = {
  key: string;
  eventType: ClosingPackageTimelineEventType;
  journalAction: string;
  title: string;
  description: string;
  timestamp: string;
  actor: string;
  result: ClosingPackageTimelineResult;
  resultLabel: string;
  cycle: number | null;
  cycleLabel: string | null;
  stageLabel: string;
  isLatest: boolean;
};

const LOW_LEVEL_COMPOSITION_ACTIONS = new Set(['ADD', 'EXCLUDE', 'REPLACE', 'RESTORE']);

const LIFECYCLE_ACTIONS = new Set([
  'CONFIRM_FORMATION',
  'SCAN_DISPATCH_REGISTERED',
  'CLIENT_APPROVED',
  'CLIENT_REJECTED',
  'ORIGINALS_PREPARED',
  'ORIGINALS_DISPATCHED',
  'CLIENT_RECEIVED',
  'ORIGINALS_RECEIPT_PROBLEM',
  'ORIGINALS_REVISION_CONFIRMED',
  'EDO_SENT',
  'EDO_RECEIPT_CONFIRMED',
  'EDO_CONTENT_REJECTED',
]);

function asRecord(value: unknown): Record<string, unknown> | null {
  return value != null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function toPositiveInt(value: unknown): number | null {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.trunc(n);
}

export function isInitialCompositionJournalEntry(entry: ClosingPackageJournalEntry): boolean {
  return (
    entry.action === 'ADD' &&
    (entry.comment === 'INITIAL_COMPOSITION_CREATED' ||
      asRecord(entry.newValue)?.kind === 'INITIAL_COMPOSITION_CREATED')
  );
}

export function resolveClosingPackageTimelineActor(entry: ClosingPackageJournalEntry): string {
  const name = String(entry.userName || '').trim();
  if (name) return name;
  if (entry.userId != null) return `Пользователь #${entry.userId}`;
  return 'Система';
}

function resolveEventType(entry: ClosingPackageJournalEntry): ClosingPackageTimelineEventType {
  if (isInitialCompositionJournalEntry(entry)) {
    return 'INITIAL_COMPOSITION_CREATED';
  }
  if (LIFECYCLE_ACTIONS.has(entry.action)) {
    return entry.action as ClosingPackageTimelineEventType;
  }
  return 'UNKNOWN';
}

function isLowLevelCompositionEntry(entry: ClosingPackageJournalEntry): boolean {
  if (isInitialCompositionJournalEntry(entry)) return false;
  return LOW_LEVEL_COMPOSITION_ACTIONS.has(entry.action);
}

function problemTypeLabel(problemType: unknown): string | null {
  const key = String(problemType || '').trim();
  if (!key) return null;
  return ORIGINALS_PROBLEM_TYPE_OPTIONS.find((option) => option.value === key)?.label ?? key;
}

function extractCycle(
  eventType: ClosingPackageTimelineEventType,
  entry: ClosingPackageJournalEntry
): { cycle: number | null; cycleLabel: string | null } {
  const newValue = asRecord(entry.newValue);
  const oldValue = asRecord(entry.oldValue);

  if (
    eventType === 'SCAN_DISPATCH_REGISTERED' ||
    eventType === 'CLIENT_APPROVED' ||
    eventType === 'CLIENT_REJECTED' ||
    eventType === 'EDO_CONTENT_REJECTED' ||
    eventType === 'CONFIRM_FORMATION'
  ) {
    const cycle =
      toPositiveInt(newValue?.cycle) ??
      toPositiveInt(oldValue?.cycle) ??
      toPositiveInt(newValue?.approvalCycle);
    return cycle
      ? { cycle, cycleLabel: `Цикл согласования: ${cycle}` }
      : { cycle: null, cycleLabel: null };
  }

  if (
    eventType === 'ORIGINALS_PREPARED' ||
    eventType === 'ORIGINALS_DISPATCHED' ||
    eventType === 'ORIGINALS_REVISION_CONFIRMED' ||
    eventType === 'ORIGINALS_RECEIPT_PROBLEM' ||
    eventType === 'CLIENT_RECEIVED' ||
    eventType === 'EDO_SENT' ||
    eventType === 'EDO_RECEIPT_CONFIRMED'
  ) {
    const originalsCycle =
      toPositiveInt(newValue?.originalsCycle) ??
      toPositiveInt(newValue?.sealedCycleNumber) ??
      toPositiveInt(newValue?.cycle) ??
      toPositiveInt(oldValue?.originalsCycle);
    if (originalsCycle) {
      return { cycle: originalsCycle, cycleLabel: `Цикл оригиналов: ${originalsCycle}` };
    }
    const approvalCycle = toPositiveInt(newValue?.approvalCycle);
    return approvalCycle
      ? { cycle: approvalCycle, cycleLabel: `Цикл согласования: ${approvalCycle}` }
      : { cycle: null, cycleLabel: null };
  }

  return { cycle: null, cycleLabel: null };
}

function presentation(
  eventType: ClosingPackageTimelineEventType,
  entry: ClosingPackageJournalEntry
): {
  title: string;
  description: string;
  result: ClosingPackageTimelineResult;
  resultLabel: string;
  stageLabel: string;
} {
  const newValue = asRecord(entry.newValue);

  switch (eventType) {
    case 'INITIAL_COMPOSITION_CREATED': {
      const count = Number(newValue?.itemCount ?? 0);
      return {
        title: 'Создание комплекта',
        description: count > 0 ? `Создан начальный состав: ${count} поз.` : 'Создан начальный состав комплекта',
        result: 'success',
        resultLabel: 'Выполнено',
        stageLabel: 'Формирование',
      };
    }
    case 'CONFIRM_FORMATION': {
      const count = Number(newValue?.itemCount ?? 0);
      return {
        title: 'Комплект сформирован',
        description:
          count > 0
            ? `Состав комплекта подтверждён (${count} поз.)`
            : 'Состав комплекта подтверждён',
        result: 'success',
        resultLabel: 'Выполнено',
        stageLabel: 'Формирование',
      };
    }
    case 'SCAN_DISPATCH_REGISTERED':
      return {
        title: 'Сканы отправлены клиенту',
        description: 'Электронный комплект отправлен клиенту на согласование',
        result: 'success',
        resultLabel: 'Выполнено',
        stageLabel: 'Согласование',
      };
    case 'CLIENT_APPROVED':
      return {
        title: 'Согласовано клиентом',
        description: 'Получен положительный ответ клиента',
        result: 'success',
        resultLabel: 'Согласовано',
        stageLabel: 'Согласование',
      };
    case 'CLIENT_REJECTED':
      return {
        title: 'Комплект отклонён',
        description: entry.comment?.trim()
          ? `Получены замечания клиента: ${entry.comment.trim()}`
          : 'Получены замечания клиента, создан цикл доработки',
        result: 'rejection',
        resultLabel: 'Доработка',
        stageLabel: 'Согласование',
      };
    case 'ORIGINALS_PREPARED':
      return {
        title: 'Оригиналы подготовлены',
        description: 'Бумажные оригиналы подготовлены к отправке',
        result: 'success',
        resultLabel: 'Выполнено',
        stageLabel: 'Оригиналы',
      };
    case 'ORIGINALS_DISPATCHED':
      return {
        title: 'Оригиналы отправлены клиенту',
        description: 'Бумажные оригиналы отправлены клиенту',
        result: 'success',
        resultLabel: 'Отправлено',
        stageLabel: 'Оригиналы',
      };
    case 'CLIENT_RECEIVED':
      return {
        title: 'Клиент получил оригиналы',
        description: 'Клиент получил оригиналы',
        result: 'success',
        resultLabel: 'Получено',
        stageLabel: 'Оригиналы',
      };
    case 'EDO_SENT':
      return {
        title: 'Документы отправлены через ЭДО',
        description: 'Комплект отправлен клиенту через ЭДО',
        result: 'success',
        resultLabel: 'Отправлено',
        stageLabel: 'ЭДО',
      };
    case 'EDO_RECEIPT_CONFIRMED':
      return {
        title: 'Клиент принял документы по ЭДО',
        description: 'Бухгалтер подтвердил принятие документов клиентом в ЭДО',
        result: 'success',
        resultLabel: 'Принято',
        stageLabel: 'ЭДО',
      };
    case 'EDO_CONTENT_REJECTED':
      return {
        title: 'Комплект отклонён клиентом, требуется исправление',
        description: entry.comment?.trim()
          ? `Зафиксирована необходимость доработки: ${entry.comment.trim()}`
          : 'Зафиксирована необходимость доработки комплекта по ЭДО',
        result: 'rejection',
        resultLabel: 'Доработка',
        stageLabel: 'ЭДО',
      };
    case 'ORIGINALS_RECEIPT_PROBLEM': {
      const problem = problemTypeLabel(newValue?.problemType);
      return {
        title: 'Проблема с получением оригиналов',
        description: problem
          ? `Зафиксирована проблема получения: ${problem}`
          : 'Зафиксирована проблема получения, создан новый цикл оригиналов',
        result: 'problem',
        resultLabel: 'Проблема',
        stageLabel: 'Оригиналы',
      };
    }
    case 'ORIGINALS_REVISION_CONFIRMED':
      return {
        title: 'Доработка оригиналов завершена',
        description: 'Доработанный состав оригиналов подтверждён',
        result: 'success',
        resultLabel: 'Выполнено',
        stageLabel: 'Оригиналы',
      };
    default:
      return {
        title: entry.action || 'Операция комплекта',
        description: entry.comment?.trim() || `Операция ${entry.action || 'без типа'}`,
        result: 'neutral',
        resultLabel: 'Зафиксировано',
        stageLabel: 'Операция',
      };
  }
}

function compareJournalEntries(a: ClosingPackageJournalEntry, b: ClosingPackageJournalEntry): number {
  const aTs = Date.parse(a.createdAt);
  const bTs = Date.parse(b.createdAt);
  const aValid = !Number.isNaN(aTs);
  const bValid = !Number.isNaN(bTs);
  if (aValid && bValid && aTs !== bTs) return aTs - bTs;
  return a.id - b.id;
}

/**
 * Maps journal rows to Timeline v1 items.
 * Low-level composition ADD/EXCLUDE/REPLACE/RESTORE stay in Journal only
 * (except INITIAL_COMPOSITION_CREATED). Unknown future actions are shown, not dropped.
 */
export function buildClosingPackageTimelineItems(
  entries: ClosingPackageJournalEntry[] | null | undefined
): ClosingPackageTimelineItem[] {
  const source = Array.isArray(entries) ? entries : [];
  const selected = source.filter((entry) => !isLowLevelCompositionEntry(entry));
  const ordered = [...selected].sort(compareJournalEntries);

  return ordered.map((entry, index) => {
    const eventType = resolveEventType(entry);
    const view = presentation(eventType, entry);
    const cycle = extractCycle(eventType, entry);

    return {
      key: String(entry.id),
      eventType,
      journalAction: entry.action,
      title: view.title,
      description: view.description,
      timestamp: entry.createdAt,
      actor: resolveClosingPackageTimelineActor(entry),
      result: view.result,
      resultLabel: view.resultLabel,
      cycle: cycle.cycle,
      cycleLabel: cycle.cycleLabel,
      stageLabel: view.stageLabel,
      isLatest: index === ordered.length - 1,
    };
  });
}
